import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function EmailCaptureGate({ onSubmit, toolName }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSubmit(email);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="email-capture-gate">
      <div className="max-w-md w-full bg-[#1e2438] border border-white/10 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-400">
            <rect width="20" height="16" x="2" y="4" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Your results are ready!</h2>
        <p className="text-white/60 mb-6 text-sm">
          Enter your email to see your {toolName} results. We'll also send a copy to your inbox.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            data-testid="email-capture-input"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 bg-[#141824] border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-amber-500/50 text-sm"
          />
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <button
            data-testid="email-capture-submit"
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-xl transition-colors text-sm disabled:opacity-50"
          >
            {loading ? 'Revealing...' : 'Reveal My Results'}
          </button>
        </form>
        <p className="text-white/30 text-xs mt-4">No spam. Unsubscribe anytime.</p>
      </div>
    </div>
  );
}

export function TeaserResult({ archetypeName, percentage, score, correct, total, percentile, toolType }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="teaser-result">
      <div className="max-w-md w-full bg-[#1e2438] border border-white/10 rounded-2xl p-8 text-center">
        {toolType === 'quiz' ? (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">&#127917;</span>
            </div>
            <p className="text-amber-400 text-sm font-medium mb-1">Your Gambler Personality</p>
            <h2 className="text-3xl font-bold text-white mb-2">{archetypeName}</h2>
            <p className="text-white/50 text-sm mb-6">Only {percentage}% of players share this type</p>
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl font-bold text-amber-400">{score}</span>
            </div>
            <p className="text-amber-400 text-sm font-medium mb-1">Casino IQ Score</p>
            <h2 className="text-3xl font-bold text-white mb-2">{score}/100</h2>
            <p className="text-white/50 text-sm mb-6">You beat {percentile}% of players ({correct}/{total} correct)</p>
          </>
        )}

        <div className="bg-[#141824] border border-white/10 rounded-xl p-4 mb-6">
          <p className="text-white/70 text-sm">
            Create a free account to unlock your <span className="text-amber-400 font-medium">full personalized report</span>, 
            19 smart tools, and exclusive bonuses.
          </p>
        </div>

        <button
          data-testid="teaser-create-account-btn"
          onClick={() => navigate('/signup')}
          className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-xl transition-colors text-sm mb-3"
        >
          Create Free Account
        </button>
        <button
          data-testid="teaser-login-btn"
          onClick={() => navigate('/login')}
          className="w-full py-2.5 bg-transparent border border-white/20 hover:border-white/40 text-white/70 font-medium rounded-xl transition-colors text-sm"
        >
          Already have an account? Log in
        </button>
      </div>
    </div>
  );
}
